import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const counties = ["Sonoma", "Napa", "Marin", "Lake", "Mendocino"];

export default function AuthPage({ setUser }) {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    role: "student",
    county: "",
    trade_focus: ""
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const [agreedToGuidelines, setAgreedToGuidelines] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate policy agreements for signup
    if (!isLogin) {
      if (!agreedToTerms || !agreedToPrivacy || !agreedToGuidelines) {
        toast.error("You must agree to all policies before creating an account");
        return;
      }
    }
    
    setLoading(true);

    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      setUser(response.data.user);
      toast.success(isLogin ? "Welcome back!" : "Account created successfully!");
      navigate("/jobs");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #4ECDC4 100%)' }}>
      <Card className="w-full max-w-md shadow-2xl" data-testid="auth-card">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-teal-500 flex items-center justify-center text-white text-2xl font-bold">
              NT
            </div>
          </div>
          <CardTitle className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
            NextGen Trades Academy
          </CardTitle>
          <CardDescription className="text-base">
            {isLogin ? "Welcome back to the community" : "Join the NextGen Trades Academy community"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    data-testid="name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required={!isLogin}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">I am a...</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                  >
                    <SelectTrigger data-testid="role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="student">Current Student</SelectItem>
                      <SelectItem value="graduate">Graduate/Alumni</SelectItem>
                      <SelectItem value="employer">Employer/Contractor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.role === 'employer' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-900">
                      <strong>Employer Account:</strong> Submit job postings for admin approval. All posts are reviewed before going live.
                    </p>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="county">County</Label>
                  <Select
                    value={formData.county}
                    onValueChange={(value) => setFormData({ ...formData, county: value })}
                  >
                    <SelectTrigger data-testid="county-select">
                      <SelectValue placeholder="Select county" />
                    </SelectTrigger>
                    <SelectContent>
                      {counties.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="trade_focus">Trade Focus</Label>
                  <Input
                    id="trade_focus"
                    data-testid="trade-focus-input"
                    placeholder="e.g., Electrical, Plumbing, HVAC"
                    value={formData.trade_focus}
                    onChange={(e) => setFormData({ ...formData, trade_focus: e.target.value })}
                  />
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="email-input"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="password-input"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
              />
            </div>
            {!isLogin && (
              <div className="space-y-3 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm font-semibold text-gray-900 mb-3">
                  Please review and agree to the following (required):
                </p>
                
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    id="agree-terms"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-1 h-4 w-4 text-teal-600 rounded border-gray-300 focus:ring-teal-500"
                    data-testid="agree-terms-checkbox"
                  />
                  <label htmlFor="agree-terms" className="text-sm text-gray-700">
                    I have read and agree to the{" "}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-teal-600 underline font-medium hover:text-teal-700">
                      Terms of Service
                    </a>
                  </label>
                </div>

                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    id="agree-privacy"
                    checked={agreedToPrivacy}
                    onChange={(e) => setAgreedToPrivacy(e.target.checked)}
                    className="mt-1 h-4 w-4 text-teal-600 rounded border-gray-300 focus:ring-teal-500"
                    data-testid="agree-privacy-checkbox"
                  />
                  <label htmlFor="agree-privacy" className="text-sm text-gray-700">
                    I have read and agree to the{" "}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-teal-600 underline font-medium hover:text-teal-700">
                      Privacy Policy
                    </a>
                    {" "}and understand my California privacy rights (CCPA)
                  </label>
                </div>

                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    id="agree-guidelines"
                    checked={agreedToGuidelines}
                    onChange={(e) => setAgreedToGuidelines(e.target.checked)}
                    className="mt-1 h-4 w-4 text-teal-600 rounded border-gray-300 focus:ring-teal-500"
                    data-testid="agree-guidelines-checkbox"
                  />
                  <label htmlFor="agree-guidelines" className="text-sm text-gray-700">
                    I have read and agree to the{" "}
                    <a href="/guidelines" target="_blank" rel="noopener noreferrer" className="text-teal-600 underline font-medium hover:text-teal-700">
                      Community Guidelines
                    </a>
                    {" "}including the zero-tolerance harassment policy
                  </label>
                </div>

                <p className="text-xs text-gray-600 mt-2 pt-2 border-t border-amber-200">
                  <strong>California Notice:</strong> This platform is subject to California law including Civil Code §1708.5 (harassment prohibition) and CCPA (consumer privacy rights).
                </p>
              </div>
            )}
            
            <Button
              type="submit"
              className="w-full mt-4"
              disabled={loading || (!isLogin && (!agreedToTerms || !agreedToPrivacy || !agreedToGuidelines))}
              data-testid="auth-submit-button"
              style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #4ECDC4 100%)', color: 'white' }}
            >
              {loading ? "Loading..." : isLogin ? "Sign In" : "Create Account"}
            </Button>
          </form>
          <div className="mt-4 text-center text-sm">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-teal-600 hover:underline font-medium"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? "Need an account? Sign up" : "Already have an account? Sign in"}
            </button>
          </div>
          <div className="mt-3 text-center text-xs text-gray-500">
            Admin? Use admin credentials to access admin panel
          </div>
        </CardContent>
      </Card>
    </div>
  );
}